import { useState } from 'react';
import { TeButton, TeContainer } from '@topcon/tuicl-react';
import { useNotification } from '@topcon/topcon-utils';

import {
  useTaxonomy,
  useSubscriptions,
} from '../Settings/Notifications/notificationPreferences.queries';
import { useUpdateNotificationPreference } from '../Settings/Notifications/notificationPreferences.mutations';
import { Section } from '../../utils/styling';

const Playground = () => {
  const [showResults, setShowResults] = useState(false);
  const [dataType, setDataType] = useState<'taxonomy' | 'subscriptions'>('taxonomy');
  const notificationService = useNotification();

  const {
    data: taxonomyData,
    isLoading: taxonomyLoading,
    error: taxonomyError,
    refetch: refetchTaxonomy,
  } = useTaxonomy();
  const {
    data: subscriptionsData,
    isLoading: subscriptionsLoading,
    error: subscriptionsError,
    refetch: refetchSubscriptions,
  } = useSubscriptions('user');

  const updateNotificationPreference = useUpdateNotificationPreference();

  const handleFetchTaxonomy = () => {
    setShowResults(true);
    setDataType('taxonomy');
    refetchTaxonomy();
  };

  const handleFetchSubscriptions = () => {
    setShowResults(true);
    setDataType('subscriptions');
    refetchSubscriptions();
  };

  const handleSubscribe = () => {
    const payload = {
      channels: { email: true, in_app: true },
      classes: {
        // ciam: { channels: true },
        'ciam.invite': { channels: { email: false, in_app: false, sms: false } },
      },
    };

    updateNotificationPreference.mutate(
      { subscriptions: payload, aspect: 'user' },
      {
        onSuccess: () => {
          setShowResults(true);
          setDataType('subscriptions');
          // Refresh subscriptions data to show the updated state
          refetchSubscriptions();
        },
        onError: (error) => {
          console.error('Subscription update failed:', error);
        },
      }
    );
  };

  const isLoading = dataType === 'taxonomy' ? taxonomyLoading : subscriptionsLoading;
  const error = dataType === 'taxonomy' ? taxonomyError : subscriptionsError;
  const data = dataType === 'taxonomy' ? taxonomyData : subscriptionsData;

  return (
    <Section $background="background.transparent" $alignment="center" $padding="10px">
      <TeContainer
        column
        gap="s"
        alignItems="center"
        backgroundColor="background.primary"
        borderRadius="m"
      >
        <TeContainer gap="s" fill>
          <TeButton
            text="Info test"
            variant="primary"
            teclick={() => {
              notificationService.addNotification('InfoTest', 'This is an info test', 'info');
            }}
          />
          <TeButton
            text="Success test"
            variant="secondary"
            teclick={() => {
              notificationService.addNotification(
                'SuccessTest',
                'This is a success test',
                'success'
              );
            }}
          />
          <TeButton
            text="Error test"
            variant="tertiary"
            teclick={() => {
              notificationService.addNotification('ErrorTest', 'This is an error test', 'error');
            }}
          />
        </TeContainer>
        <TeContainer column grow padding="xs" gap="xs" overflow="auto" alignItems="center">
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <button onClick={handleFetchTaxonomy} disabled={isLoading}>
              {taxonomyLoading ? 'Loading...' : 'Fetch Taxonomy'}
            </button>
            <button onClick={handleFetchSubscriptions} disabled={isLoading}>
              {subscriptionsLoading ? 'Loading...' : 'Fetch User Subscriptions'}
            </button>
            <button
              onClick={handleSubscribe}
              disabled={updateNotificationPreference.isPending}
              style={{ backgroundColor: '#4CAF50', color: 'white' }}
            >
              {updateNotificationPreference.isPending
                ? 'Subscribing...'
                : 'Subscribe to CIAM & TNE'}
            </button>
          </div>

          {updateNotificationPreference.isError && (
            <div style={{ color: 'red', marginBottom: '1rem' }}>
              Subscription Error:{' '}
              {updateNotificationPreference.error?.message || 'Unknown error occurred'}
            </div>
          )}

          {updateNotificationPreference.isSuccess && (
            <div style={{ color: 'green', marginBottom: '1rem' }}>
              Successfully subscribed to CIAM & TNE notifications!
            </div>
          )}

          {showResults && (
            <div style={{ width: '100%', maxWidth: '800px' }}>
              <h3>
                {dataType === 'taxonomy' ? 'Taxonomy Results:' : 'User Subscriptions Results:'}
              </h3>
              {error && (
                <div style={{ color: 'red', marginBottom: '1rem' }}>Error: {error.message}</div>
              )}
              {data && (
                <pre
                  style={{
                    backgroundColor: '#f5f5f5',
                    padding: '1rem',
                    borderRadius: '4px',
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  <code>{JSON.stringify(data, null, 2)}</code>
                </pre>
              )}
            </div>
          )}
        </TeContainer>
      </TeContainer>
    </Section>
  );
};

export default Playground;
